import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@repo/ui/src/components/ui/card';
import { Alert, AlertDescription } from '@repo/ui/src/components/ui/alert';
import { Button } from '@repo/ui/src/components/ui/button';
import { Switch } from '@repo/ui/src/components/ui/switch';
import { Input } from '@repo/ui/src/components/ui/input';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { Banknote } from 'lucide-react';
import { useOrgSettings, useUpdatePreferences, useUpdateOrgSettings } from '../../../hooks/useOrgSettings';
import { useChargeAbsentSplit, useChargeAbsentBulk } from '../../../hooks/useEnrollments';
import { useSubscription } from '../../../hooks/useSubscription';
import type { UpdateOrgSettingsInput } from '@repo/validation';
import {
  DEFAULT_INVOICE_MESSAGE_TEMPLATE,
  resolveTemplateSaveValue,
} from '../../../lib/invoiceMessage';
import { InvoiceTemplateEditor } from '../components/InvoiceTemplateEditor';
import { SectionLoading } from '../components/SectionLoading';
import { BusyButton } from '../../../components/BusyButton';

/** Anchor the "Sửa mẫu tin nhắn" link (SendInvoiceDialog) can scroll to —
 *  see the template Card below and the hash-scroll effect in BillingRulesForm. */
const TEMPLATE_CARD_ANCHOR_ID = 'invoice-message-template';

/** Every card in this section carries the money-territory border (spec §4) —
 *  no background wash, just the ochre hairline. */
const MONEY_CARD = 'border-attention/30';

/** Inner form component — mounts with correct initial values from server */
function BillingRulesForm({
  initialDefaultChargeAbsent,
  initialMaxExcusedPerMonth,
  initialInvoiceMessageTemplate,
  initialShowInvoiceWatermark,
  initialInvoiceDueDay,
  initialAutoOverdueReminder,
}: {
  initialDefaultChargeAbsent: boolean;
  initialMaxExcusedPerMonth: number | null;
  initialInvoiceMessageTemplate: string | null;
  initialShowInvoiceWatermark: boolean;
  initialInvoiceDueDay: number | null;
  initialAutoOverdueReminder: boolean;
}) {
  const { t } = useTranslation('settings');
  // The one field here that is saved through `useUpdatePreferences`
  // (POST /auth/onboarding) rather than PUT /org/settings — see handleSave.
  const [defaultChargeAbsent, setDefaultChargeAbsent] = useState(initialDefaultChargeAbsent);
  const [hasChargeAbsentChanges, setHasChargeAbsentChanges] = useState(false);
  const [maxExcusedPerMonth, setMaxExcusedPerMonth] = useState<number | null>(initialMaxExcusedPerMonth);
  // Its own flag, not folded into `hasChargeAbsentChanges`: the apply-all
  // bulk-sweep path (handleConfirmApplyAll) resets that flag on success but
  // must never touch the quota field (see useUpdateOrgSettings) — sharing one
  // flag would make that path silently clear the "unsaved" indicator for a
  // still-unsaved quota edit.
  const [hasQuotaChanges, setHasQuotaChanges] = useState(false);
  const [applyAllOpen, setApplyAllOpen] = useState(false);

  // Manual-send message template (THI-87 §3) — same partial-update route as
  // the quota field above, but its own dirty flag for the same reason
  // (see the chargeAbsentApplyAll sweep comment): it must never be swept
  // into that bulk path, and a quota-only save must not falsely mark the
  // template "saved". `templateText` is what the editor/preview render;
  // `storedTemplate` tracks what's actually persisted (starts at the value
  // GET returned, and is updated — not just left at the initial prop — after
  // every successful save, so `resolveTemplateSaveValue` stays correct across
  // more than one save in the same mount; see handleSave).
  const [templateText, setTemplateText] = useState(
    initialInvoiceMessageTemplate ?? DEFAULT_INVOICE_MESSAGE_TEMPLATE,
  );
  const [storedTemplate, setStoredTemplate] = useState<string | null>(initialInvoiceMessageTemplate);
  const [hasTemplateChanges, setHasTemplateChanges] = useState(false);

  // Watermark toggle (THI-90) — the org's own preference, same partial-update
  // route as the quota/template fields above but its own dirty flag for the
  // same reason: it must never ride along in the chargeAbsentApplyAll sweep,
  // and saving it must not falsely mark the other fields "saved" or vice
  // versa.
  const [showInvoiceWatermark, setShowInvoiceWatermark] = useState(initialShowInvoiceWatermark);
  const [hasWatermarkChanges, setHasWatermarkChanges] = useState(false);

  // Default invoice due-day (THI-100 follow-up) — same partial-update route
  // and own-dirty-flag pattern as the quota/template/watermark fields above,
  // for the same reason: it must never ride along in the chargeAbsentApplyAll
  // sweep, and saving it must not falsely mark the other fields "saved" or
  // vice versa. This is the removal path for a default the chốt-sổ dialog's
  // save-default checkbox can set but never clear.
  const [invoiceDueDay, setInvoiceDueDay] = useState<number | null>(initialInvoiceDueDay);
  const [hasDueDayChanges, setHasDueDayChanges] = useState(false);

  // Overdue reminder emails (2026-10-12): opt-in, same partial-update route
  // and own-dirty-flag pattern as the watermark toggle.
  const [autoOverdueReminder, setAutoOverdueReminder] = useState(initialAutoOverdueReminder);
  const [hasReminderChanges, setHasReminderChanges] = useState(false);

  // Both mutations run `silent` because this section owns the single toast for
  // the whole save action (spec §4's double-toast fix) — see handleSave. Their
  // ERROR toasts still fire, so a failed save is never silent.
  const updatePreferences = useUpdatePreferences({ silent: true });
  const updateOrgSettings = useUpdateOrgSettings();
  const chargeAbsentSplit = useChargeAbsentSplit(applyAllOpen);
  const chargeAbsentBulk = useChargeAbsentBulk({ silent: true });
  const { data: sub } = useSubscription();
  // Fails closed while the subscription hasn't loaded yet (`?? true`) so the
  // switch never renders enabled before we actually know the plan — matching
  // the API's own fail-closed default for a missing subscription.
  const watermarkForcedByPlan = sub?.plan.showsWatermark ?? true;

  function handleToggleDefaultChargeAbsent(next: boolean) {
    setDefaultChargeAbsent(next);
    setHasChargeAbsentChanges(true);
  }

  // Empty string ⇄ null: the input's own value is `maxExcusedPerMonth ?? ''`,
  // so an emptied field must round-trip back to null (no cap), not NaN or 0.
  function handleMaxExcusedChange(raw: string) {
    setMaxExcusedPerMonth(raw === '' ? null : Number(raw));
    setHasQuotaChanges(true);
  }

  function handleTemplateTextChange(next: string) {
    setTemplateText(next);
    setHasTemplateChanges(true);
  }

  function handleToggleWatermark(next: boolean) {
    setShowInvoiceWatermark(next);
    setHasWatermarkChanges(true);
  }

  // Empty string ⇄ null: mirrors handleMaxExcusedChange above — an emptied
  // field must round-trip back to null (no default; the +7-days rule
  // stands), not NaN or 0.
  function handleInvoiceDueDayChange(raw: string) {
    setInvoiceDueDay(raw === '' ? null : Number(raw));
    setHasDueDayChanges(true);
  }

  // The send dialog's "Sửa mẫu tin nhắn" link navigates to
  // `/settings/billing-rules#invoice-message-template` — land scrolled to the
  // template card instead of just the top of the section.
  useEffect(() => {
    if (window.location.hash === `#${TEMPLATE_CARD_ANCHOR_ID}`) {
      document.getElementById(TEMPLATE_CARD_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const isDirty =
    hasChargeAbsentChanges ||
    hasQuotaChanges ||
    hasTemplateChanges ||
    hasWatermarkChanges ||
    hasDueDayChanges ||
    hasReminderChanges;
  const isSaving = updatePreferences.isPending || updateOrgSettings.isPending;

  // Fires both mutations, each gated on its own dirty flag — see
  // useUpdateOrgSettings for why the quota field can't just ride along inside
  // the preferences payload. Gating avoids re-POSTing an unchanged
  // `defaultChargeAbsent` whenever only the quota was edited, and vice versa.
  // Each mutation still clears its OWN dirty flags in its own onSuccess, so a
  // half-failed save leaves exactly the unsaved half marked dirty; the single
  // success toast fires only once every mutation this click asked for has
  // resolved. On a rejection the hooks have already toasted their own error —
  // we must not also claim success.
  async function handleSave() {
    const requests: Promise<unknown>[] = [];

    if (hasChargeAbsentChanges) {
      requests.push(
        updatePreferences.mutateAsync(
          { defaultChargeAbsent },
          { onSuccess: () => setHasChargeAbsentChanges(false) },
        ),
      );
    }

    if (hasQuotaChanges || hasTemplateChanges || hasWatermarkChanges || hasDueDayChanges || hasReminderChanges) {
      const payload: UpdateOrgSettingsInput = {};
      if (hasReminderChanges) payload.autoOverdueReminder = autoOverdueReminder;
      if (hasQuotaChanges) payload.maxExcusedPerMonth = maxExcusedPerMonth;
      if (hasWatermarkChanges) payload.showInvoiceWatermark = showInvoiceWatermark;
      if (hasDueDayChanges) payload.invoiceDueDay = invoiceDueDay;

      // `resolveTemplateSaveValue` returns `undefined` for "the text is
      // already the default and nothing was stored before" — that case must
      // stay OUT of the payload (an omitted field is untouched by the
      // partial-update route), not sent as a literal `undefined`.
      const nextStoredTemplate = hasTemplateChanges
        ? resolveTemplateSaveValue(templateText, storedTemplate)
        : undefined;
      if (hasTemplateChanges && nextStoredTemplate !== undefined) {
        payload.invoiceMessageTemplate = nextStoredTemplate;
      }

      requests.push(
        updateOrgSettings.mutateAsync(payload, {
          onSuccess: () => {
            setHasQuotaChanges(false);
            setHasWatermarkChanges(false);
            setHasDueDayChanges(false);
            setHasReminderChanges(false);
            if (hasTemplateChanges) {
              setHasTemplateChanges(false);
              // Keep `storedTemplate` in sync with what's now actually
              // persisted so a second edit-and-save in the same mount (e.g.
              // custom → default → custom again) keeps computing the right
              // undefined/null/string branch above.
              if (nextStoredTemplate !== undefined) setStoredTemplate(nextStoredTemplate);
            }
          },
        }),
      );
    }

    // Defensive: the Save button is disabled while every dirty flag is false,
    // so this is unreachable from the UI — but an empty Promise.all would
    // resolve instantly and toast "saved" for a save that wrote nothing.
    if (requests.length === 0) return;

    try {
      await Promise.all(requests);
    } catch {
      return;
    }
    toast.success(t('saved'));
  }

  // One teacher intention — "from now on unexcused absences are/aren't charged" —
  // covers both new enrollments (the default) and every existing one (the sweep), so
  // the confirm action does both, not two separate steps that could leave the saved
  // default and the swept enrollments disagreeing. The save runs FIRST and the sweep
  // only fires on its success: a sweep applied under an unsaved default is exactly
  // the bug this fixes, so the reverse order must never happen. Anyone who wants only
  // the default keeps using the ordinary "Lưu thay đổi" button below. One action, one
  // toast: both mutations are silent and the count comes from the bulk result.
  async function handleConfirmApplyAll() {
    let updated: number;
    try {
      await updatePreferences.mutateAsync(
        { defaultChargeAbsent },
        { onSuccess: () => setHasChargeAbsentChanges(false) },
      );
      const result = await chargeAbsentBulk.mutateAsync({ chargeAbsent: defaultChargeAbsent });
      updated = result.updated;
    } catch {
      return;
    }
    setApplyAllOpen(false);
    toast.success(t('savedApplied', { count: updated }));
  }

  return (
    <div className="space-y-6">
      <Alert className="border-attention/30">
        <Banknote className="text-attention-foreground" strokeWidth={1.9} />
        <AlertDescription>{t('billingRules.alert')}</AlertDescription>
      </Alert>

      {/* Tính tiền buổi vắng không phép — default for new enrollments + apply to existing ones */}
      <Card className={MONEY_CARD}>
        <CardHeader>
          <CardTitle className="text-base">{t('preferences.chargeAbsentTitle')}</CardTitle>
          <CardDescription>{t('preferences.chargeAbsentDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Switch
            checked={defaultChargeAbsent}
            onCheckedChange={handleToggleDefaultChargeAbsent}
            aria-label={t('preferences.chargeAbsentTitle')}
          />
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs font-medium"
              onClick={() => setApplyAllOpen(true)}
            >
              {t('preferences.chargeAbsentApplyAll')}
            </Button>
          </div>

          {/* Excused-absence quota — nested under charge-absent since the cap
              only matters for enrollments that bill their absences. Saved via
              a separate route/mutation (see useUpdateOrgSettings), fired
              together with this section's other fields by the button below. */}
          <div className="space-y-1.5 border-l border-surface-200 pl-4">
            <label htmlFor="maxExcusedPerMonth" className="text-sm font-medium text-surface-700">
              {t('preferences.maxExcusedTitle')}
            </label>
            <Input
              id="maxExcusedPerMonth"
              type="number"
              min={0}
              value={maxExcusedPerMonth ?? ''}
              onChange={(e) => handleMaxExcusedChange(e.target.value)}
              className="w-24"
            />
            <p className="text-xs text-surface-500">{t('preferences.maxExcusedHint')}</p>
          </div>
        </CardContent>
      </Card>

      {/* Mẫu tin nhắn gửi hóa đơn — THI-87 §3. Saved via the same
          useUpdateOrgSettings mutation as the quota field above, but its own
          dirty flag (see hasTemplateChanges) — it must never ride along in
          the chargeAbsentApplyAll sweep. */}
      <Card id={TEMPLATE_CARD_ANCHOR_ID} className={MONEY_CARD}>
        <CardHeader>
          <CardTitle className="text-base">{t('preferences.templateTitle')}</CardTitle>
          <CardDescription>{t('preferences.templateDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <InvoiceTemplateEditor value={templateText} onChange={handleTemplateTextChange} />
        </CardContent>
      </Card>

      {/* Ngày hạn thanh toán mặc định — THI-100 follow-up. Placed right
          after the message template card (which itself carries the
          {han_thanh_toan} token) and before the watermark card: all three are
          invoice-generation-time defaults, whereas the quota field in the
          charge-absent card is scoped to absence-charging instead. Own Card,
          own dirty flag (hasDueDayChanges), saved via the same
          useUpdateOrgSettings mutation — this is the only removal path for a
          default the chốt-sổ dialog's save-default checkbox can set but never
          clear. */}
      <Card className={MONEY_CARD}>
        <CardHeader>
          <CardTitle className="text-base">{t('preferences.invoiceDueDayTitle')}</CardTitle>
          <CardDescription>{t('preferences.invoiceDueDayHint')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            id="invoiceDueDay"
            type="number"
            min={1}
            max={28}
            value={invoiceDueDay ?? ''}
            onChange={(e) => handleInvoiceDueDayChange(e.target.value)}
            aria-label={t('preferences.invoiceDueDayTitle')}
            className="w-24"
          />
        </CardContent>
      </Card>

      {/* Nhắc quá hạn qua email (2026-10-12). Off by default: the cron used
          to mail every overdue invoice with a parent email every morning,
          including ones the teacher sent by hand on Zalo. Own Card, own dirty
          flag, saved via the same useUpdateOrgSettings mutation. */}
      <Card className={MONEY_CARD}>
        <CardHeader>
          <CardTitle className="text-base">{t('preferences.autoReminderTitle')}</CardTitle>
          <CardDescription>{t('preferences.autoReminderDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Switch
            checked={autoOverdueReminder}
            onCheckedChange={(next) => {
              setAutoOverdueReminder(next);
              setHasReminderChanges(true);
            }}
            aria-label={t('preferences.autoReminderTitle')}
          />
        </CardContent>
      </Card>

      {/* Watermark hóa đơn — THI-90. Its own Card, its own dirty flag
          (hasWatermarkChanges), saved via the same useUpdateOrgSettings
          mutation as the quota/template fields above. Forced on for a plan
          whose showsWatermark is true (Free/trial-expired) — read from
          useSubscription(), the same source the tier gates elsewhere in
          settings use. */}
      <Card className={MONEY_CARD}>
        <CardHeader>
          <CardTitle className="text-base">{t('preferences.watermarkTitle')}</CardTitle>
          <CardDescription>{t('preferences.watermarkDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Switch
            checked={watermarkForcedByPlan ? true : showInvoiceWatermark}
            disabled={watermarkForcedByPlan}
            onCheckedChange={(next) => !watermarkForcedByPlan && handleToggleWatermark(next)}
            aria-label={t('preferences.watermarkTitle')}
          />
          {watermarkForcedByPlan && (
            <p className="mt-1 text-xs text-muted-foreground">{t('preferences.watermarkForcedNote')}</p>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={applyAllOpen} onOpenChange={setApplyAllOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('preferences.chargeAbsentApplyConfirmTitle')}</AlertDialogTitle>
            {/* Split into three fragments (rather than one interpolated
                sentence) so the still-loading count can be an actual
                <Skeleton/>, not a "…" string — asChild swaps Radix's default
                <p> for a <div>, since Skeleton renders a block-level <div>
                and a <p> ancestor would otherwise get silently closed early
                by the browser, splitting the sentence in two. */}
            <AlertDialogDescription asChild>
              <div>
                {t('preferences.chargeAbsentApplyConfirmBody1')}{' '}
                {chargeAbsentSplit.split ? (
                  chargeAbsentSplit.split.monthly
                ) : (
                  <Skeleton className="inline-block h-3 w-4 align-middle" />
                )}{' '}
                {t('preferences.chargeAbsentApplyConfirmBody2')}{' '}
                {chargeAbsentSplit.split ? (
                  chargeAbsentSplit.split.term
                ) : (
                  <Skeleton className="inline-block h-3 w-4 align-middle" />
                )}{' '}
                {t('preferences.chargeAbsentApplyConfirmBody3')}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updatePreferences.isPending || chargeAbsentBulk.isPending}>
              {t('preferences.chargeAbsentApplyCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmApplyAll}
              disabled={!chargeAbsentSplit.split || updatePreferences.isPending || chargeAbsentBulk.isPending}
            >
              {t('preferences.chargeAbsentApplyConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex justify-end">
        <BusyButton
          onClick={handleSave}
          disabled={!isDirty}
          busy={isSaving}
          busyLabel={t('preferences.saving')}
          className="h-10 px-6 text-sm font-semibold"
        >
          {t('save')}
        </BusyButton>
      </div>
    </div>
  );
}

export function BillingRulesSection() {
  const { data: orgSettings, isLoading } = useOrgSettings();

  if (isLoading || !orgSettings) return <SectionLoading />;

  return (
    <BillingRulesForm
      initialDefaultChargeAbsent={orgSettings.defaultChargeAbsent ?? false}
      initialMaxExcusedPerMonth={orgSettings.maxExcusedPerMonth}
      initialInvoiceMessageTemplate={orgSettings.invoiceMessageTemplate}
      initialShowInvoiceWatermark={orgSettings.showInvoiceWatermark}
      initialInvoiceDueDay={orgSettings.invoiceDueDay}
      initialAutoOverdueReminder={orgSettings.autoOverdueReminder ?? false}
    />
  );
}
